import { toast } from 'sonner';
import CheckIcon from '@mui/icons-material/Check';
import React, { useEffect, useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardOutlinedIcon from '@mui/icons-material/KeyboardOutlined';
import { Typography, Box, IconButton, Tooltip, Collapse } from '@mui/material';

import { mfaColors } from '../styles';

/**
 * @param {Object} props
 * @param {string | undefined} props.secretKey
 */
export const SecretKeyDisplayer = ({ secretKey }) => {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!copied) {
      return undefined;
    }
    const timeout = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timeout);
  }, [copied]);

  const handleCopy = async () => {
    if (!secretKey) {
      return;
    }

    toast.promise(navigator.clipboard.writeText(secretKey), {
      loading: 'Copying...',
      success: () => {
        setCopied(true);
        return 'Secret key copied to clipboard';
      },
      error: 'Failed to copy',
    });
  };

  if (!secretKey) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: 2,
        border: `1px solid ${mfaColors.border}`,
        overflow: 'hidden',
      }}
    >
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          cursor: 'pointer',
          backgroundColor: expanded ? mfaColors.background : mfaColors.surface,
          transition: 'background-color 0.2s ease',
          '&:hover': {
            backgroundColor: mfaColors.background,
          },
        }}
      >
        <KeyboardOutlinedIcon sx={{ color: mfaColors.textMuted, fontSize: 20 }} />
        <Typography variant="body2" sx={{ fontWeight: 500, color: mfaColors.text, flex: 1 }}>
          Can&apos;t scan the QR code?
        </Typography>
        <Typography variant="caption" sx={{ color: mfaColors.primary, fontWeight: 500 }}>
          {expanded ? 'Hide' : 'Show'} manual entry
        </Typography>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ p: 2, pt: 0 }}>
          <Typography variant="caption" sx={{ color: mfaColors.textMuted, display: 'block', mb: 1.5 }}>
            Enter this secret key manually in your authenticator app:
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
              borderRadius: 1.5,
              backgroundColor: mfaColors.background,
              border: `1px solid ${mfaColors.border}`,
            }}
          >
            <Typography
              sx={{
                flex: 1,
                fontFamily: 'monospace',
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: mfaColors.text,
                wordBreak: 'break-all',
                letterSpacing: '0.05em',
              }}
            >
              {secretKey}
            </Typography>

            <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'} arrow>
              <IconButton
                onClick={handleCopy}
                size="small"
                sx={{
                  color: copied ? mfaColors.success : mfaColors.primary,
                  backgroundColor: copied ? `${mfaColors.success}15` : 'transparent',
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: copied ? `${mfaColors.success}20` : `${mfaColors.primary}10`,
                  },
                }}
              >
                {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};
